import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { age as validateAge, contact as validateContact, name as validateName } from '../../utils/validations';
import './PatientForm.scss';

const staticText = {
  title: 'Patient Details',
  patientNameLabel: 'Patient Name',
  contactLabel: 'Contact Number ',
  ageLabel: 'Age ',
  genderLabel: 'Gender ',
  genderOptions: ['Male', 'Female', 'Other'],
  errInvalidName: 'Name cannot contain numbers or special characters',
  errInvalidContact: 'Please enter a valid 10 digit number',
  errAgeRequired: 'Age is required',
  errInvalidAge: 'Please enter a valid age (1-99)',
  errGenderRequired: 'Please select a gender',
};

class PatientForm extends Component {
  constructor(props) {
    super(props);
    const { patient } = props;
    this.state = {
      name: patient.name || '',
      contact: patient.contact || '',
      age: patient.age || '',
      gender: patient.gender || '',
      focusedField: null,
      errors: {
        name: '',
        contact: '',
        age: '',
        gender: '',
      },
    };
  }

  handleInputChange = (field, value) => {
    const { onChange } = this.props;
    this.setState({ [field]: value }, () => {
      if (['name', 'contact', 'age'].includes(field)) {
        this.validateField(field);
      }
      const patientData = {
        name: this.state.name,
        contact: this.state.contact,
        age: this.state.age,
        gender: this.state.gender,
      };
      if (onChange) {
        onChange(patientData);
      }
    });
  }

  handleGenderSelect = (gender) => {
    const { onChange } = this.props;
    this.setState({ gender, errors: { ...this.state.errors, gender: '' } }, () => {
      const patientData = {
        name: this.state.name,
        contact: this.state.contact,
        age: this.state.age,
        gender: this.state.gender,
      };
      if (onChange) {
        onChange(patientData);
      }
    });
  }

  validateField = (field) => {
    const { errors } = this.state;
    const value = this.state[field];
    let error = '';

    switch (field) {
      case 'name':
        if (value && !validateName(value)) {
          error = staticText.errInvalidName;
        }
        break;

      case 'contact':
        if (!value) {
          if (this.props.contactRequired !== false) {
            error = staticText.errInvalidContact;
          }
        } else if (!validateContact(value)) {
          error = staticText.errInvalidContact;
        }
        break;

      case 'age':
        if (!value) {
          if (this.props.ageRequired) {
            error = staticText.errAgeRequired;
          }
        } else if (!validateAge(value)) {
          error = staticText.errInvalidAge;
        }
        break;

      case 'gender':
        if (!value && this.props.genderRequired) {
          error = staticText.errGenderRequired;
        }
        break;

      default:
        break;
    }

    this.setState({
      errors: {
        ...errors,
        [field]: error,
      },
    });

    return error === '';
  }

  validateAll = () => {
    const { contactRequired, ageRequired, genderRequired } = this.props;
    const isNameValid = this.validateField('name');
    const isContactValid = contactRequired !== false
      ? this.validateField('contact')
      : (this.validateField('contact'), true); // optional — shows error if filled incorrectly, but doesn't block
    const isAgeValid = ageRequired
      ? this.validateField('age')
      : (this.validateField('age'), true); // optional — shows error if filled incorrectly, but doesn't block
    const isGenderValid = genderRequired
      ? this.validateField('gender')
      : true;
    return isNameValid && isContactValid && isAgeValid && isGenderValid;
  }

  shouldFloat = (field) => {
    const { focusedField } = this.state;
    const value = this.state[field];
    return !!(value || focusedField === field);
  }

  render() {
    const { name, contact, age, gender, errors } = this.state;
    const { contactRequired, ageRequired, genderRequired } = this.props;

    return (
      <div className="pdContainer">
        <p className="pdTitle">{staticText.title}</p>

        <div className="pdInputsWrapper">
          <div className="pdField">
            <label className={'pdLabel' + (this.shouldFloat('name') ? ' pdLabelFloat' : '')}>{staticText.patientNameLabel}</label>
            <input
              className={'pdInput' + (errors.name ? ' pdInputError' : '')}
              type="text"
              value={name}
              onFocus={() => this.setState({ focusedField: 'name' })}
              onBlur={() => this.setState({ focusedField: null })}
              onChange={e => this.handleInputChange('name', e.target.value)}
            />
            {errors.name && <p className="pdError">{errors.name}</p>}
          </div>

          <div className="pdRow">
            <div className="pdFieldContact">
              <label className={'pdLabel' + (this.shouldFloat('contact') ? ' pdLabelFloat' : '')}>{staticText.contactLabel}{contactRequired !== false ? '*' : ''}</label>
              <input
                className={'pdInput' + (errors.contact ? ' pdInputError' : '')}
                type="tel"
                maxLength={10}
                value={contact}
                onFocus={() => this.setState({ focusedField: 'contact' })}
                onBlur={() => this.setState({ focusedField: null })}
                onChange={e => this.handleInputChange('contact', e.target.value.replace(/\D/g, ''))}
              />
              {errors.contact && <p className="pdError">{errors.contact}</p>}
            </div>

            <div className="pdFieldAge">
              <label className={'pdLabel' + (this.shouldFloat('age') ? ' pdLabelFloat' : '')}>{staticText.ageLabel}{ageRequired ? '*' : ''}</label>
              <input
                className={'pdInput' + (errors.age ? ' pdInputError' : '')}
                min="1"
                max="99"
                type="number"
                value={age}
                onFocus={() => this.setState({ focusedField: 'age' })}
                onBlur={() => this.setState({ focusedField: null })}
                onKeyDown={e => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                onChange={e => this.handleInputChange('age', e.target.value)}
              />
              {errors.age && <p className="pdError">{errors.age}</p>}
            </div>
          </div>
        </div>

        <div className="pdGenderWrapper">
          <label className="pdGenderLabel">{staticText.genderLabel}{genderRequired ? '*' : ''}</label>
          <div className="pdChips">
            {staticText.genderOptions.map(genderOption => (
              <div
                key={genderOption}
                className={'pdChip' + (gender === genderOption ? ' pdChipActive' : '')}
                onClick={() => this.handleGenderSelect(genderOption)}
              >
                {genderOption}
              </div>
            ))}
          </div>
          {errors.gender && <p className="pdError">{errors.gender}</p>}
        </div>
      </div>
    );
  }
}

PatientForm.propTypes = {
  patient: PropTypes.shape({
    name: PropTypes.string,
    contact: PropTypes.string,
    age: PropTypes.string,
    gender: PropTypes.string,
  }),
  onChange: PropTypes.func,
  contactRequired: PropTypes.bool,
  ageRequired: PropTypes.bool,
  genderRequired: PropTypes.bool,
};

PatientForm.defaultProps = {
  patient: {
    name: '',
    contact: '',
    age: '',
    gender: '',
  },
  onChange: null,
};

export default PatientForm;
